// Shared event-row presentation, used by both the Logs and Trash panes so a
// record reads identically in either place: a one-line human summary, the
// colored event-type icon (from eventMeta), and a severity pill where relevant.

import type { CustomPtExercise, GymExercisePayload, MedicalEvent } from "@medassist/shared";
import {
  formatGymDistance,
  formatMedStrength,
  foodSummaryLine,
  hydrationSummaryLine,
  getPtExerciseById,
  isPrivateEvent,
  BODY_METRICS,
  PRIVATE_ACTIVITY_LABEL,
  TREATMENT_EFFECT_LABELS,
  summarizeBowelMovement,
  type BowelMovementPayload,
} from "@medassist/shared";
import { EVENT_META, eventTypeLabel, symptomEventBadge } from "./eventMeta.js";

// `events` is only needed to resolve a `pt_session.exerciseId` that points at a
// custom (scanned-sheet) exercise, whose name lives on the linked `pt_program`
// event rather than the bundled catalog. Omit it and custom sessions fall back
// to the raw exercise id.
export function summarize(e: MedicalEvent, events?: MedicalEvent[]): string {
  const p = e.payload as Record<string, unknown>;
  // D-ACT-6: private entries render as a generic label everywhere `summarize` is
  // used (Logs, recent-activity widget, report citations). The Activity page has
  // its own reveal toggle and does not go through this function for those rows.
  if (isPrivateEvent(e)) {
    const mins = p.durationMin;
    return typeof mins === "number" ? `${PRIVATE_ACTIVITY_LABEL} · ${mins} min` : PRIVATE_ACTIVITY_LABEL;
  }
  if (e.eventType === "medication_taken") {
    const strength = formatMedStrength(p as Parameters<typeof formatMedStrength>[0]);
    const strengthPart = strength ? `${strength} ` : "";
    return `${p.name} ${strengthPart}${p.route}`.trim();
  }
  if (e.eventType === "symptom") {
    const region = p.region ? `, ${p.region}` : "";
    if (p.severity === 0 || p.resolvedAt) return `${p.name}${region} — resolved`;
    return `${p.name} · severity ${p.severity}/10${region}`;
  }
  if (e.eventType === "vital_sign") {
    const reading = p.kind === "bp" ? `${p.systolic}/${p.diastolic} ${p.unit}` : `${p.value} ${p.unit}`;
    const detail = [p.position, p.arm && `${p.arm} arm`, p.method, p.context, p.state]
      .filter(Boolean)
      .join(", ");
    return `${p.kind}: ${reading}${detail ? ` (${detail})` : ""}`;
  }
  if (e.eventType === "headache") {
    const loc = p.location ? `, ${p.location}` : "";
    if (p.severity === 0 || p.resolvedAt) return `headache${loc} — resolved`;
    const assoc = [
      p.nausea && "nausea",
      p.photophobia && "light-sensitivity",
      p.phonophobia && "sound-sensitivity",
      p.aura && p.aura !== "none" && `${p.aura} aura`,
      p.neckStiffness && "neck-stiffness",
    ].filter(Boolean);
    const withPart = assoc.length ? ` · ${assoc.join(", ")}` : "";
    return `headache · severity ${p.severity}/10${loc}${withPart}`;
  }
  if (e.eventType === "sleep") {
    const hrs = p.totalSleepHours != null ? `${p.totalSleepHours}h` : "";
    const extras = [
      p.awakenings != null && `${p.awakenings} awakening${p.awakenings === 1 ? "" : "s"}`,
      p.aidUsed && `aid: ${p.aidUsed}`,
      p.dreamRecall && p.dreamRecall !== "none" && `${p.dreamRecall} dreams`,
    ]
      .filter(Boolean)
      .join(", ");
    const label = p.kind === "nap" ? "nap" : "sleep";
    return `${label} · ${hrs}, quality ${p.qualityRating}/10${extras ? ` · ${extras}` : ""}`;
  }
  if (e.eventType === "food") return foodSummaryLine(p);
  if (e.eventType === "hydration") return hydrationSummaryLine(p);
  if (e.eventType === "stress_event") {
    const cat = p.category ? ` (${p.category})` : "";
    const ongoing = p.ongoing ? ", ongoing" : "";
    return `${p.description}${cat} · severity ${p.severity}/10${ongoing}`;
  }
  if (e.eventType === "exercise") {
    const intensity = p.intensity ? ` ${p.intensity}` : "";
    const dist = p.distanceMi != null ? `, ${p.distanceMi} mi` : "";
    const rpe = p.perceivedExertionRpe != null ? `, RPE ${p.perceivedExertionRpe}/10` : "";
    return `${p.durationMin} min${intensity} ${p.kind}${dist}${rpe}`;
  }
  if (e.eventType === "mood") {
    const subs = [
      p.anxiety != null && `anxiety ${p.anxiety}`,
      p.energy != null && `energy ${p.energy}`,
      p.focus != null && `focus ${p.focus}`,
      p.irritability != null && `irritability ${p.irritability}`,
    ]
      .filter(Boolean)
      .join(", ");
    return `mood ${p.mood}/10${subs ? ` · ${subs}` : ""}`;
  }
  if (e.eventType === "visit") {
    const reason = p.reasonForVisit ? ` for ${p.reasonForVisit}` : "";
    const dx = Array.isArray(p.diagnoses) && p.diagnoses.length ? ` — dx: ${(p.diagnoses as string[]).join(", ")}` : "";
    return `${p.visitType} · ${p.provider}${reason}${dx}`;
  }
  if (e.eventType === "vaccination") {
    const dose = p.doseInSeries ? ` (${p.doseInSeries})` : "";
    const mfr = p.manufacturer ? `, ${p.manufacturer}` : "";
    return `${p.name}${dose}${mfr}`;
  }
  if (e.eventType === "illness") {
    const sev = p.severity ? `, ${p.severity}` : "";
    return `${p.name} (${p.status}${sev})`;
  }
  if (e.eventType === "family_history") {
    const rel = String(p.relation ?? "").replace(/-/g, " ");
    const age = p.onsetAge != null ? ` (onset ~${p.onsetAge})` : "";
    const flag = p.hereditaryConcern ? " ⚑" : "";
    return `${rel}: ${p.condition}${age}${flag}`;
  }
  if (e.eventType === "allergy") {
    const sev = p.severityHistorical ? `, ${p.severityHistorical}` : "";
    const rx = Array.isArray(p.reactionType) && p.reactionType.length ? ` — ${(p.reactionType as string[]).join(", ")}` : "";
    return `${p.substance} (${p.category}${sev})${rx}`;
  }
  if (e.eventType === "bowel_movement") {
    const { headline, flags } = summarizeBowelMovement(p as Partial<BowelMovementPayload>);
    const flagText = flags.map((f) => f.label).join(", ");
    return `${headline}${flagText ? ` — ${flagText}` : ""}`;
  }
  if (e.eventType === "environmental_exposure") {
    const ongoing = p.ongoing ? ", ongoing" : "";
    return `${p.kind}: ${p.description}${ongoing}`;
  }
  if (e.eventType === "surgery_or_procedure") {
    const where = p.facility ? ` at ${p.facility}` : "";
    return `${p.name}${where}`;
  }
  if (e.eventType === "hospitalization") {
    const status = p.dischargedAt ? "discharged" : "still admitted";
    return `${p.facility} — ${p.reason} (${status})`;
  }
  if (e.eventType === "lab_result") {
    const ref = p.refLow != null || p.refHigh != null ? ` (ref ${p.refLow ?? "?"}–${p.refHigh ?? "?"})` : "";
    const flag = p.abnormalFlag && p.abnormalFlag !== "N" ? ` [${p.abnormalFlag}]` : "";
    return `${p.analyte} ${p.value} ${p.unit}${ref}${flag}`;
  }
  if (e.eventType === "clinical_note") {
    const preview = String(p.notesPreview ?? p.body ?? "");
    const title = typeof p.title === "string" && p.title ? p.title : null;
    if (title) return preview ? `${title} — ${preview}` : title;
    return preview;
  }
  if (e.eventType === "pt_program") {
    const isScanned = p.source === "scanned";
    const count = isScanned
      ? Array.isArray(p.customExercises) ? p.customExercises.length : 0
      : Array.isArray(p.exerciseIds) ? p.exerciseIds.length : 0;
    const title = isScanned
      ? (typeof p.sheetTitle === "string" && p.sheetTitle) || "Scanned PT program"
      : `PT program${p.bodyRegion ? ` — ${p.bodyRegion}` : ""}`;
    const cadence = typeof p.cadence === "string" && p.cadence ? ` · ${p.cadence}` : "";
    return `${title} · ${count} exercise${count === 1 ? "" : "s"}${cadence}`;
  }
  if (e.eventType === "gym_program") {
    const days = Array.isArray(p.days) ? (p.days as Array<{ exercises?: unknown[] }>) : [];
    const exerciseCount = days.reduce((n, d) => n + (Array.isArray(d.exercises) ? d.exercises.length : 0), 0);
    const title = typeof p.programTitle === "string" && p.programTitle ? p.programTitle : "Workout program";
    return `${title} · ${days.length} day${days.length === 1 ? "" : "s"}, ${exerciseCount} exercise${exerciseCount === 1 ? "" : "s"}`;
  }
  if (e.eventType === "body_composition") {
    const metrics = p.metrics as Record<string, { value: number; unit?: string }> | undefined;
    const parts: string[] = [];
    for (const id of ["bodyFatPct", "leanMassPct", "estWeight", "waist"]) {
      const m = metrics?.[id];
      if (!m) continue;
      const def = BODY_METRICS.find((d) => d.id === id);
      parts.push(`${def?.label ?? id} ${m.value}${m.unit ?? def?.unit ?? ""}`);
      if (parts.length === 2) break;
    }
    const shape = typeof p.bodyShapeClass === "string" && p.bodyShapeClass ? ` (${p.bodyShapeClass})` : "";
    return parts.length ? `Progress photo · ${parts.join(", ")}${shape}` : `Progress photo${shape}`;
  }
  if (e.eventType === "body_measurement") {
    const sites = p.sites as Record<string, { cm: number }> | undefined;
    const count = sites ? Object.keys(sites).length : 0;
    const waist = sites?.waist_narrow?.cm ?? sites?.abdomen?.cm;
    const waistPart = waist != null ? `waist ${waist} cm` : undefined;
    const rest = count > (waistPart ? 1 : 0) ? `${count - (waistPart ? 1 : 0)} more site${count - (waistPart ? 1 : 0) === 1 ? "" : "s"}` : undefined;
    const parts = [waistPart, rest].filter(Boolean);
    return parts.length ? `Measurements · ${parts.join(", ")}` : `Measurements · ${count} site${count === 1 ? "" : "s"}`;
  }
  if (e.eventType === "gym_exercise") {
    const parts: string[] = [];
    if (p.sets != null && p.reps != null) parts.push(`${p.sets}×${p.reps}`);
    else if (p.sets != null) parts.push(`${p.sets} sets`);
    else if (p.reps != null) parts.push(`${p.reps} reps`);
    if (p.weight != null) parts.push(`@ ${p.weight} ${p.weightUnit ?? "lbs"}`);
    if (p.durationMin != null) parts.push(`${p.durationMin} min`);
    const gymDist = formatGymDistance(p as Pick<GymExercisePayload, "distanceMi" | "distanceUnit">);
    if (gymDist) parts.push(gymDist);
    if (p.laps != null) parts.push(`${p.laps} laps`);
    if (p.resistanceLevel != null) parts.push(`resistance ${p.resistanceLevel}`);
    if (p.incline != null) parts.push(`incline ${p.incline}%`);
    const sideSuffix = p.side === "left" ? " (left)" : p.side === "right" ? " (right)" : "";
    return `${p.name}${parts.length ? ` — ${parts.join(", ")}` : ""}${sideSuffix}`;
  }
  if (e.eventType === "pt_session") {
    if (p.skipped) return "PT skipped";
    const exerciseId = typeof p.exerciseId === "string" ? p.exerciseId : undefined;
    if (!exerciseId) return "PT exercise completed — whole program";
    const catalogMatch = getPtExerciseById(exerciseId);
    if (catalogMatch) return `PT exercise completed — ${catalogMatch.name}`;
    const program = events?.find(
      (ev) => ev.eventId === p.programEventId && ev.eventType === "pt_program"
    );
    const customExercises = program?.payload as { customExercises?: CustomPtExercise[] } | undefined;
    const custom = customExercises?.customExercises?.find((c) => c.id === exerciseId);
    return `PT exercise completed — ${custom?.name ?? exerciseId}`;
  }
  if (e.eventType === "treatment") {
    const forSymptom = p.forSymptomName ? ` for ${p.forSymptomName}` : "";
    const duration = p.durationMin ? `, ${p.durationMin} min` : "";
    const effect = typeof p.effect === "string" ? p.effect as keyof typeof TREATMENT_EFFECT_LABELS : undefined;
    const effectPart = effect ? ` — ${TREATMENT_EFFECT_LABELS[effect]}` : "";
    return `${p.name}${forSymptom}${duration}${effectPart}`;
  }
  if (e.eventType === "injury") {
    const region = p.bodyRegion ? `, ${p.bodyRegion}` : "";
    const side = p.side && p.side !== "n/a" ? ` (${p.side})` : "";
    const status = p.status ? ` — ${p.status}` : "";
    return `${p.suspectedDiagnosis || p.aiSummary || "Injury"}${region}${side}${status}`;
  }
  return JSON.stringify(p);
}

// Severity-style score (0–10) for the types where a colored accent pill reads
// better than inline text. Returns null for types without one.
// Severity 0 is the resolution sentinel (D-SF-3) — skip the pill; the badge handles it.
function severityValue(e: MedicalEvent): number | null {
  const p = e.payload as Record<string, unknown>;
  if (e.eventType === "symptom" || e.eventType === "headache" || e.eventType === "stress_event") {
    const v = typeof p.severity === "number" ? p.severity : null;
    return v === 0 ? null : v;
  }
  if (e.eventType === "mood") {
    return typeof p.mood === "number" ? p.mood : null;
  }
  return null;
}

// Color band for a 0–10 score: 1–3 calm, 4–6 moderate, 7–10 high.
function severityBandClass(v: number): string {
  if (v <= 3) return "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300";
  if (v <= 6) return "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300";
  return "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300";
}

// Remove the inline "severity N/10" / "mood N/10" text once it's shown as a
// pill, then tidy the separators it leaves behind.
function stripSeverity(summary: string): string {
  let s = summary.replace(/severity \d+\/10/, "").replace(/mood \d+\/10/, "mood");
  s = s.replace(/ · ,/g, ","); // "X · , Y" → "X, Y"
  s = s.replace(/\s+/g, " ").trim();
  s = s.replace(/ ·$/g, "").trim(); // drop a dangling trailing separator
  return s;
}

// Overdose-guard indicator for a medication_taken event flagged at capture time
// (MEDICATION_OVERDOSE_PLAN.md). Set server-side, go-forward only — absent on
// events logged before this field existed; never retroactively computed.
export function isOverdoseFlagged(e: MedicalEvent): boolean {
  return !!overdoseBadge(e);
}

function overdoseBadge(e: MedicalEvent): { label: string; className: string } | null {
  if (e.eventType !== "medication_taken") return null;
  const sev = (e.payload as Record<string, unknown>).overdoseSeverity;
  if (sev !== "caution" && sev !== "warning" && sev !== "critical") return null;
  return {
    label: "⚠️ overdose risk",
    className: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
  };
}

// Free-text note saved alongside the event (dashboard quick-log "add a note").
function noteText(e: MedicalEvent): string | null {
  const p = e.payload as Record<string, unknown>;
  return typeof p.notes === "string" && p.notes.trim() ? p.notes.trim() : null;
}

// One event row's text content (icon + summary + severity pill), shared by the
// mobile and desktop layouts of the Logs and Trash panes.
export function EntryBody({ e, events }: { e: MedicalEvent; events?: MedicalEvent[] }) {
  const meta = EVENT_META[e.eventType];
  const Icon = meta?.Icon;
  const label = eventTypeLabel(e.eventType);
  const sev = severityValue(e);
  const text = sev != null ? stripSeverity(summarize(e, events)) : summarize(e, events);
  const badge = symptomEventBadge(e);
  const overdose = overdoseBadge(e);
  const note = noteText(e);
  return (
    <span className="block min-w-0 flex-1">
      {Icon && (
        <span
          className={`mr-2 inline-block align-middle ${meta.color}`}
          title={label}
          aria-label={label}
        >
          <Icon size={16} />
        </span>
      )}
      <span className="break-words align-middle text-gray-900 dark:text-gray-100">{text}</span>
      {sev != null && (
        <span
          className={`ml-2 inline-block align-middle rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums ${severityBandClass(sev)}`}
          title={`Severity ${sev} of 10`}
        >
          {sev}/10
        </span>
      )}
      {badge && (
        <span className={`ml-2 inline-block align-middle rounded-full px-1.5 py-0.5 text-xs font-medium ${badge.className}`}>
          {badge.label}
        </span>
      )}
      {overdose && (
        <span
          className={`ml-2 inline-block align-middle rounded-full px-1.5 py-0.5 text-xs font-medium ${overdose.className}`}
          title="This dose was flagged by the overdose guard when it was logged"
        >
          {overdose.label}
        </span>
      )}
      {note && (
        <span className="mt-1 block break-words text-xs italic text-gray-500 dark:text-gray-400">
          “{note}”
        </span>
      )}
    </span>
  );
}
